import { buildAuthorizeUrl, type AuthorizeUrlParams } from './authorizeUrl'
import { getZIdentityConfig } from './config'
import { translate } from './i18n'
import { INFO_ICON_SVG, ZUCCHETTI_MARK_SVG } from './icons'
import { buttonStyles } from './style'

export interface RenderLoginButtonOptions extends Omit<AuthorizeUrlParams, 'clientId'> {
  /** Falls back to the authority set via configureZIdentity() when omitted. */
  authority?: string
  /** Falls back to the clientId set via configureZIdentity() when omitted. */
  clientId?: string
  locale?: string
  showInfo?: boolean
  minimal?: boolean
}

// Tracks the ResizeObserver created for a given host element (see matchButtonWidth below) so a
// re-render — attribute change, extraParams setter, locale switch — disconnects the previous
// one instead of leaking it; a WeakMap means the entry disappears on its own once the host is
// garbage-collected.
const resizeObservers = new WeakMap<HTMLElement, ResizeObserver>()

/** Disconnects the ResizeObserver (if any) tracking this host — call from disconnectedCallback. */
export function disposeLoginButton(target: HTMLElement): void {
  resizeObservers.get(target)?.disconnect()
  resizeObservers.delete(target)
}

// redirectUri has no global-config fallback: it's per-page, not an app-wide default, so
// buildAuthorizeUrl() (unchanged) keeps requiring it explicitly, same as clientId below.
export function renderLoginButton(target: HTMLElement, options: RenderLoginButtonOptions): void {
  disposeLoginButton(target)

  const config = getZIdentityConfig()
  const authority = options.authority ?? config?.authority
  const clientId = options.clientId ?? config?.clientId

  if (!authority) {
    throw new Error('renderLoginButton: "authority" is required (pass it directly, or set it via configureZIdentity)')
  }
  if (!clientId) {
    throw new Error('renderLoginButton: "clientId" is required (pass it directly, or set it via configureZIdentity)')
  }

  const authorizeUrl = buildAuthorizeUrl(authority, { ...options, clientId })

  const shadow = target.shadowRoot ?? target.attachShadow({ mode: 'open' })
  shadow.innerHTML = ''

  const style = document.createElement('style')
  style.textContent = buttonStyles
  shadow.appendChild(style)

  const button = buildButton(authorizeUrl, options.locale, options.minimal)
  shadow.appendChild(button)

  if (!options.minimal && options.showInfo) {
    const infoBox = buildInfoBox(options.locale)
    shadow.appendChild(infoBox)
    matchButtonWidth(target, button, infoBox)
  }
}

// The info box is a block of text: left to size itself it would end up far wider than the
// button, so its width follows the button's. Tracked rather than measured once at render,
// because at that moment the button may not be measurable at all — still detached, inside a
// display:none ancestor, or laid out against a web font that hasn't loaded yet — and a single
// early read would pin it to 0px forever.
function matchButtonWidth(target: HTMLElement, button: HTMLButtonElement, infoBox: HTMLDivElement): void {
  const sync = (): void => {
    const width = button.offsetWidth
    if (width > 0) {
      infoBox.style.width = `${width}px`
    }
  }

  sync()
  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(sync)
    observer.observe(button)
    resizeObservers.set(target, observer)
  }
}

function buildButton(
  authorizeUrl: string,
  locale: string | undefined,
  minimal: boolean | undefined,
): HTMLButtonElement {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = minimal ? 'zid-login-button zid-login-button--minimal' : 'zid-login-button'

  const icon = document.createElement('span')
  icon.className = 'zid-login-button__icon'
  icon.innerHTML = ZUCCHETTI_MARK_SVG
  button.append(icon)

  const loginLabel = translate('LoginButton.Label', locale)

  let label: HTMLSpanElement | undefined
  if (minimal) {
    // No visible text in this variant, so the accessible name has to come from aria-label
    // instead of the button's text content.
    button.setAttribute('aria-label', loginLabel)
  } else {
    label = document.createElement('span')
    label.className = 'zid-login-button__label'
    label.textContent = loginLabel
    button.append(label)
  }

  button.addEventListener('click', () => {
    // Pin the current width so swapping in the shorter "loading" label doesn't shrink the
    // button. Measured here, on click, because by then the button is certainly laid out.
    button.style.minWidth = `${button.offsetWidth}px`
    button.disabled = true
    if (label) {
      label.textContent = translate('LoginButton.Loading', locale)
    }
    window.location.assign(authorizeUrl)
  })

  return button
}

function buildInfoBox(locale: string | undefined): HTMLDivElement {
  const box = document.createElement('div')
  box.className = 'zid-info-box'

  const icon = document.createElement('span')
  icon.className = 'zid-info-box__icon'
  icon.innerHTML = INFO_ICON_SVG

  const text = document.createElement('div')

  const title = document.createElement('p')
  title.className = 'zid-info-box__title'
  title.textContent = translate('LoginButton.InfoTitle', locale)

  const description = document.createElement('p')
  description.className = 'zid-info-box__description'
  description.textContent = translate('LoginButton.InfoDescription', locale)

  text.append(title, description)
  box.append(icon, text)

  return box
}
